/**
 * MoneyValue — the ONE shared AVOS treatment for a monetary value shown inside a
 * summary / KPI / stat card.
 *
 * It renders the amount COMPACT (Indian K/L/Cr for INR, international K/M/B
 * otherwise) so it never wraps, and exposes the EXACT, fully-grouped amount on
 * mouse hover (native `title`) and keyboard focus (`tabIndex` + `aria-label`).
 * This is the single central implementation — screens must not reimplement
 * compact money formatting or the hover treatment inline.
 *
 * Money always arrives as integer MINOR units (bigint-safe as `number` for
 * display sizes). Formatting is delegated to the shared, pure, SSR-safe
 * `formatMoneyCompact` / `formatMoney` helpers in `../services.js`, so the
 * component is itself pure and SSR-safe (no clock/DOM reads).
 *
 * Usage:
 *   <MoneyValue minor={Number(x.revenueMinor)} currency={x.currency} />
 *   // compact + hover/focus tooltip, wrapped in a <span class="console-card__value">
 *
 * For a non-card inline figure that should still be compact-with-tooltip but
 * NOT carry the card value typography, pass `className` to override.
 */
import type { ReactElement } from "react";

import { formatMoney, formatMoneyCompact } from "../services.js";

/** Props for {@link MoneyValue}. */
export interface MoneyValueProps {
  /** The amount in integer MINOR units (e.g. 54616500 = ₹5,46,165.00). */
  readonly minor: number;
  /** ISO 4217 currency code (e.g. "INR", "GBP"). */
  readonly currency: string;
  /**
   * The element class. Defaults to the shared card-value + KPI treatment so
   * every summary card looks identical; pass a custom class for other surfaces.
   */
  readonly className?: string;
}

/**
 * A compact monetary value with the exact amount revealed on hover/focus.
 * Accessible: focusable, `aria-label` carries the exact amount, and it does not
 * rely on colour alone.
 */
export function MoneyValue({
  minor,
  currency,
  className = "console-card__value accounts-kpi__value",
}: MoneyValueProps): ReactElement {
  const exact = formatMoney(minor, currency);
  const compact = formatMoneyCompact(minor, currency);
  return (
    <span
      className={className}
      title={`Exact amount ${exact}`}
      tabIndex={0}
      aria-label={`Exact amount ${exact}`}
    >
      <span aria-hidden="true">{compact}</span>
    </span>
  );
}

export default MoneyValue;
